import type { InjectionKey } from 'vue';
import type { UserModule } from '~/types';

import type { AuthenticationService } from '~/authentication/services/AuthenticationService';
import type { UserService } from '~/users/services/UserService';
import type { GroupService } from '~/groups/services/GroupService';
import type { SummaryDashboardService } from '~/dashboard/services/SummaryDashboardService';
import type { LoginDashboardService } from '~/dashboard/services/LoginDashboardService';
import type { TrackingDetailsService } from '~/dashboard/services/TrackingDetailsService';
import type { WorkflowService } from '~/workflows/services/WorkflowService';
import type { CorrectiveActionService } from '~/correctiveActions/services/CorrectiveActionService';
import type { FormService } from '~/forms/services/FormService';
import type { WorkSiteService } from '~/worksites/services/WorkSiteService';
import { useNotificationService } from '~/notifications/composables/useNotificationService';
import { NotificationService } from '~/notifications/services/NotificationService';
import type { FeatureService } from '~/features/services/FeatureService';
import type { CategoryService } from '~/categories/services/CategoryService';
import type { DocumentService } from '~/documents/services/DocumentService';
import type { RoleService } from '~/roles/services/RoleService';
import type { CompanyService } from '~/companies/services/CompanyService';
import type { TicketTypeRequestsService } from '~/ticketWallet/services/TicketTypeRequestsService';
import type { TicketTypesService } from '~/ticketWallet/services/TicketTypesService';
import type { TicketService } from '~/ticketWallet/services/TicketService';
import type { CalendarService } from '~/calendar/services/CalendarService';
import type { ReportService } from '~/reports/services/ReportService';

export const AuthenticationServiceKey = Symbol(
  'AuthenticationService',
) as InjectionKey<AuthenticationService>;

export const UserServiceKey = Symbol(
  'UserService',
) as InjectionKey<UserService>;

export const GroupServiceKey = Symbol(
  'GroupService',
) as InjectionKey<GroupService>;

export const SummaryDashboardServiceKey = Symbol(
  'SummaryDashboardService',
) as InjectionKey<SummaryDashboardService>;

export const LoginDashboardServiceKey = Symbol(
  'LoginDashboardService',
) as InjectionKey<LoginDashboardService>;

export const TrackingDetailsServiceKey = Symbol(
  'TrackingDetailsService',
) as InjectionKey<TrackingDetailsService>;

export const WorkflowServiceKey = Symbol(
  'WorkflowService',
) as InjectionKey<WorkflowService>;

export const CorrectiveActionServiceKey = Symbol(
  'CorrectiveActionService',
) as InjectionKey<CorrectiveActionService>;

export const FormServiceKey = Symbol(
  'FormService',
) as InjectionKey<FormService>;

export const WorkSiteServiceKey = Symbol(
  'WorkSiteService',
) as InjectionKey<WorkSiteService>;

export const NotificationServiceKey = Symbol(
  'NotificationService',
) as InjectionKey<NotificationService>;

export const FeatureServiceKey = Symbol(
  'FeatureService',
) as InjectionKey<FeatureService>;
export const CategoryServiceKey = Symbol(
  'CategoryService',
) as InjectionKey<CategoryService>;
export const DocumentServiceKey = Symbol(
  'DocumentService',
) as InjectionKey<DocumentService>;

export const RoleServiceKey = Symbol(
  'RoleService',
) as InjectionKey<RoleService>;

export const CompanyServiceKey = Symbol(
  'CompanyService',
) as InjectionKey<CompanyService>;

export const TicketTypesServiceKey = Symbol(
  'TicketTypesService',
) as InjectionKey<TicketTypesService>;

export const TicketTypeRequestsServiceKey = Symbol(
  'TicketTypeRequestsService',
) as InjectionKey<TicketTypeRequestsService>;

export const TicketServiceKey = Symbol(
  'TicketService',
) as InjectionKey<TicketService>;

export const CalendarServiceKey = Symbol(
  'CalendarService',
) as InjectionKey<CalendarService>;

export const ReportServiceKey = Symbol(
  'ReportService',
) as InjectionKey<ReportService>;

export const install: UserModule = ({ app }) => {
  app.provide(AuthenticationServiceKey, useAuthenticationService());
  app.provide(UserServiceKey, useUserService());
  app.provide(GroupServiceKey, useGroupService());
  app.provide(SummaryDashboardServiceKey, useSummaryDashboardService());
  app.provide(LoginDashboardServiceKey, useLoginDashboardService());
  app.provide(TrackingDetailsServiceKey, useTrackingDetailsService());
  app.provide(WorkflowServiceKey, useWorkflowService());
  app.provide(CorrectiveActionServiceKey, useCorrectiveActionService());
  app.provide(FormServiceKey, useFormService());
  app.provide(WorkSiteServiceKey, useWorkSiteService());
  app.provide(NotificationServiceKey, useNotificationService());
  app.provide(FeatureServiceKey, useFeatureService());
  app.provide(CategoryServiceKey, useCategoryService());
  app.provide(DocumentServiceKey, useDocumentService());
  app.provide(RoleServiceKey, useRoleService());
  app.provide(CompanyServiceKey, useCompanyService());
  app.provide(TicketTypesServiceKey, useTicketTypesService());
  app.provide(
    TicketTypeRequestsServiceKey,
    useTicketTypeRequestsService(),
  );
  app.provide(TicketServiceKey, useTicketService());
  app.provide(CalendarServiceKey, useCalendarService());
  app.provide(ReportServiceKey, useReportService());
};
